import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertOctagon } from "lucide-react";

export const LOSS_REASONS = [
  { key: "price_too_high", label: "Prix trop élevé" },
  { key: "went_competitor", label: "Parti chez un concurrent" },
  { key: "abandoned", label: "Projet abandonné" },
  { key: "delay_too_long", label: "Délai trop long" },
  { key: "no_response", label: "Sans réponse" },
  { key: "other", label: "Autre (précisez)" },
];

/**
 * Modale bloquante — obligatoire pour marquer un lead comme perdu
 * OU un devis comme refusé (endpoint configurable).
 * Par défaut : POST /leads/{id}/mark-lost.
 * Pour refus devis : passer endpoint="/quotes/{quoteId}/refuse" + successKey="quote".
 * onDone(updatedResource) callback en cas de succès.
 */
export function LossReasonModal({
  open, leadId, leadName, onDone, onCancel,
  endpoint, successMessage,
}) {
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [saving, setSaving] = useState(false);
  const finalEndpoint = endpoint || `/leads/${leadId}/mark-lost`;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!reason) {
      toast.error("Sélectionnez une raison");
      return;
    }
    if (reason === "other" && !detail.trim()) {
      toast.error("Précisez la raison (champ Autre)");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(finalEndpoint, {
        reason,
        detail: detail.trim() || null,
      });
      toast.success(successMessage || "Lead marqué comme perdu");
      onDone?.(data);
      setReason("");
      setDetail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        // Bloquante : refuse la fermeture si en cours de submit, sinon délègue à onCancel
        if (saving) return;
        if (!v) onCancel?.();
      }}
    >
      <DialogContent className="border-slate-800 bg-slate-950 sm:max-w-md" data-testid="loss-reason-modal">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-slate-100">
            <AlertOctagon className="h-4 w-4 text-[#E67B6D]" /> Pourquoi ce lead est-il perdu ?
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {leadName ? `Lead : ${leadName}. ` : ""}Cette information alimente le dashboard « Pourquoi on perd » et reste agrégée.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-2">
            {LOSS_REASONS.map((r) => (
              <label
                key={r.key}
                className={`flex cursor-pointer items-center gap-2 rounded-md border p-2.5 text-sm transition ${
                  reason === r.key
                    ? "border-[#C4553B] bg-[#C4553B]/10 text-[#F5EFE7]"
                    : "border-slate-800 bg-slate-900/40 text-slate-300 hover:border-slate-700"
                }`}
                data-testid={`loss-reason-${r.key}`}
              >
                <input
                  type="radio"
                  name="loss_reason"
                  value={r.key}
                  checked={reason === r.key}
                  onChange={() => setReason(r.key)}
                  className="accent-[#C4553B]"
                />
                <span>{r.label}</span>
              </label>
            ))}
          </div>

          {reason === "other" && (
            <Textarea
              value={detail}
              onChange={(e) => setDetail(e.target.value.slice(0, 500))}
              placeholder="Décrivez la raison (500 caractères max)"
              className="min-h-[80px] border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="loss-reason-detail"
              maxLength={500}
              required
            />
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onCancel?.()}
              disabled={saving}
              className="text-slate-400"
              data-testid="loss-reason-cancel"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={saving || !reason}
              className="bg-[#C4553B] text-white hover:bg-[#A94531]"
              data-testid="loss-reason-submit"
            >
              {saving ? "Enregistrement…" : "Marquer comme perdu"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
